import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaExclamationTriangle,
  FaTimes,
  FaSpinner,
  FaBan,
  FaCheckCircle,
} from "react-icons/fa";
import apiRequest from "../services/api";

const TenantDeactivation = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState("");

  const handleDeactivation = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (confirmationInput !== "DEACTIVATE") {
      setError(
        "Verification failed. You must type DEACTIVATE in all uppercase."
      );
      return;
    }

    setIsLoading(true);
    setIsConfirmModalOpen(false);

    try {
      const resp = await apiRequest("tenants/deactivate", {
        method: "POST",
      });

      setSuccessMsg(
        resp?.message ||
          "Company has been deactivated successfully. All user accounts have been disabled and all related data will be deleted immediately."
      );

      setConfirmationInput("");

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate("/");
    } catch (err) {
      setError(
        err?.message ||
          "The platform architecture rejected the deactivation pipeline request."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-full p-4 pb-24 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 md:p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* ERROR */}
        {error && (
          <div className="flex items-center gap-2 p-4 text-xs font-medium border rounded-xl bg-red-50 border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 animate-fadeIn">
            <FaExclamationTriangle className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* SUCCESS */}
        {successMsg && (
          <div className="flex items-start gap-3 p-5 text-xs font-semibold border rounded-xl bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400 animate-fadeIn">
            <FaCheckCircle className="mt-0.5 text-lg shrink-0" />

            <div>
              <h4 className="mb-0.5 font-bold text-slate-900 dark:text-white">
                Operation Successful
              </h4>
              {successMsg}
            </div>
          </div>
        )}

        {/* DANGER ZONE */}
        <div className="p-6 space-y-6 overflow-hidden border rounded-2xl bg-white border-slate-200 shadow-sm dark:bg-slate-900 dark:border-white/10 dark:shadow-2xl">
          <div className="pb-4 border-b border-slate-200 dark:border-white/10">
            <h3 className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-rose-600 dark:text-rose-500">
              <FaExclamationTriangle />
              Danger Zone
            </h3>

            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Irreversible action regarding your account
            </p>
          </div>

          <div className="flex flex-col items-start justify-between gap-6 p-5 border md:flex-row md:items-center rounded-xl border-rose-200 bg-rose-50/60 dark:border-rose-500/10 dark:bg-rose-950/10">
            <div className="max-w-xl space-y-1">
              <p className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">
                Deactivating this workspace turns off access completely across
                the entire business network. All cashiers, active POS stations
                and other sales operations will be instantly locked out.
              </p>
            </div>

            <button
              type="button"
              disabled={isLoading || successMsg}
              onClick={() => {
                setError("");
                setConfirmationInput("");
                setIsConfirmModalOpen(true);
              }}
              className="flex items-center justify-center w-full gap-2 px-5 py-2.5 text-xs font-bold transition duration-150 border rounded-xl md:w-auto bg-rose-50 hover:bg-rose-100 border-rose-200 text-rose-600 dark:bg-rose-600/10 dark:hover:bg-rose-600/20 dark:border-rose-500/20 dark:text-rose-400 shrink-0 disabled:opacity-40"
            >
              {isLoading ? <FaSpinner className="animate-spin" /> : <FaBan />}
              Deactivate Account
            </button>
          </div>
        </div>

        {/* CONFIRMATION MODAL */}
        <div
          className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
            isConfirmModalOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          {/* OVERLAY */}
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70"
            onClick={() => setIsConfirmModalOpen(false)}
          />

          {/* DRAWER */}
          <div
            className={`relative w-full max-w-xl mx-auto p-6 space-y-6 border rounded-t-3xl shadow-2xl transition-transform duration-300 transform bg-white border-slate-200 dark:bg-slate-900 dark:border-white/10 sm:p-8 ${
              isConfirmModalOpen ? "translate-y-0" : "translate-y-full"
            }`}
          >
            {/* MODAL HEADER */}
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                <div className="flex items-center justify-center w-11 h-11 p-3 border rounded-xl bg-rose-50 border-rose-200 text-rose-600 dark:bg-rose-500/10 dark:border-rose-500/20 dark:text-rose-400 shrink-0">
                  <FaExclamationTriangle className="text-lg" />
                </div>

                <div>
                  <h3 className="text-base font-black tracking-wide uppercase text-slate-900 dark:text-white">
                    Authorization Required
                  </h3>

                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Confirm absolute deactivation of your business account
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsConfirmModalOpen(false)}
                className="p-2 transition-colors border rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-500 border-slate-200 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/10"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>

            {/* IMPACT WARNING */}
            <div className="p-4 text-[11px] leading-relaxed border rounded-xl space-y-2 bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-950/20 dark:border-rose-500/10 dark:text-rose-300">
              <p className="flex items-center gap-1.5 font-bold tracking-wider uppercase text-rose-600 dark:text-rose-400">
                ⚠️ Impacts:
              </p>

              <ul className="pl-4 space-y-1 list-disc">
                <li>All associated users will be deleted.</li>
                <li>All associated branches will be deleted.</li>
                <li>
                  All associated sales, inventory and all related business data
                  will be deleted forever.
                </li>
              </ul>
            </div>

            {/* CONFIRMATION FORM */}
            <form onSubmit={handleDeactivation} className="space-y-6">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                  Type{" "}
                  <span className="font-mono font-black select-none text-rose-600 dark:text-rose-400">
                    DEACTIVATE
                  </span>{" "}
                  to proceed
                </label>

                <input
                  type="text"
                  required
                  autoComplete="off"
                  placeholder="Type confirmation phrase here..."
                  value={confirmationInput}
                  onChange={(e) => setConfirmationInput(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-800 font-mono placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-rose-500/50"
                />
              </div>

              {/* FOOTER */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => setIsConfirmModalOpen(false)}
                  className="px-4 py-2.5 text-xs font-semibold transition rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={confirmationInput !== "DEACTIVATE"}
                  className="px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:bg-rose-100 disabled:text-rose-300 dark:disabled:bg-rose-950/50 dark:disabled:text-slate-500 text-white text-xs font-bold shadow-md shadow-rose-600/10 transition-all"
                >
                  Confirm Deactivation
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantDeactivation;
